/**
 * Date chip — Phase 4b inline atomic chip (wiki feature plan §4b).
 *
 * Inline chip rendered as `<span data-block-type="date-chip">`. Click
 * opens `<DatePickerPopover>` (native HTML5 `<input type="date">`). On
 * commit, calls `updateAttributes({date: 'YYYY-MM-DD'})`. Renders via
 * `formatNamed(i18n, ..., 'mediumDate')` so the displayed date follows
 * the user's locale.
 *
 * Schema shape — inline atomic, mirrors the status lozenge contract:
 *   - `group: 'inline'` + `inline: true` + `atom: true`
 *   - `selectable: true`, `draggable: false`, `marks: ''`
 *
 * Attribute:
 *   - `date: string | null` — ISO 8601 `YYYY-MM-DD` (date-only, no
 *     timezone). `null` means "not yet picked" — the NodeView renders
 *     a localized "(pick a date)" placeholder in subtle italic and the
 *     popover still opens to set one.
 *
 * Why ISO date-only (no `T` time, no timezone offset)?
 *   - Locale-agnostic on the wire. The same `2026-05-28` reads the same
 *     in every reader's locale; the NodeView formats for display.
 *   - Matches Confluence's date macro shape (a calendar date, not a
 *     timestamp). The card surface uses `dueDate` as a Date object with
 *     midnight UTC — equivalent for our purposes but the string form
 *     here is clearer in the JSON.
 *
 * Round-trip:
 *   - parseHTML matches `span[data-block-type="date-chip"]`.
 *   - renderHTML emits `data-date="YYYY-MM-DD"` (omitted when `null`).
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

/**
 * Strict-ish ISO date validator. The HTML5 date input emits this exact
 * shape on every browser we support; the regex catches stored corruption
 * + protects the NodeView's `new Date(...)` call from a NaN.
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE_RE.test(value)
}

export interface BuildDateChipExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildDateChipExtension({ view }: BuildDateChipExtensionOptions) {
  return Node.create({
    name: 'dateChip',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    marks: '',

    addAttributes() {
      return {
        date: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-date')
            return isIsoDate(raw) ? raw : null
          },
          renderHTML: (attrs: { date: string | null }) => {
            return attrs.date ? { 'data-date': attrs.date } : {}
          },
        },
      }
    },

    parseHTML() {
      return [{ tag: 'span[data-block-type="date-chip"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['span', { ...HTMLAttributes, 'data-block-type': 'date-chip' }]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}
